import React, { useEffect, useState } from "react";
import { Row, Col, Input, Label, Button, Table } from "reactstrap";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import Flatpickr from "react-flatpickr";
import {
  fetchCustomersList,
  fetchSessionsList,
  fetchSuppliersList,
  fetchTripsList,
  fetchVehiclesList,
} from "../../../slices/thunks";
import { createSelector } from "reselect";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { resetTrips } from "../../../slices/Trip/reducer";

const validationSchema = Yup.object({
  // customerId will be auto-filled from selected trips (still required)
  customerId: Yup.string().required("Customer (in selected trips) is required"),
  billDate: Yup.date().required("Bill date is required"),
  tripIds: Yup.array().min(1, "Select at least one trip"),
});

const GenerateExtraChargesBillForm = ({ onSubmit, onCloseForm }) => {
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // totals stored in local state
  const [totals, setTotals] = useState({
    tollCharges: 0,
    handlingCharges: 0,
    otherCharges: 0,
    grandTotal: 0,
  });

  const selector = createSelector(
    (state) => state,
    (state) => ({
      trips: state.Trip.trips,
      loading: state.Trip.loading,
      customersList: state.Customer.customers,
      vehicles: state.Vehicle.vehicles,

      suppliers: state.Supplier.suppliers,
      // activeSessions: state.sessions.selectedSessionYear,
      activeSessions: state.Session.selectedSessionYear,
    })
  );

  const {
    trips,
    loading,
    customersList,
    suppliers,
    activeSessions,

    vehicles,
  } = useSelector(selector);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // fetch data
  useEffect(() => {
    dispatch(
      fetchCustomersList({
        isPagination: 0,
      })
    );
    dispatch(
      fetchVehiclesList({
        isPagination: 0,
      })
    );
    // dispatch(
    //   fetchSessionsList({
    //     isPagination: 0,
    //     isActive: true,
    //   })
    // );
    dispatch(
      fetchSuppliersList({
        isPagination: 0,
      })
    );
  }, [dispatch]);

  const calculateTotals = (selectedTripIds = []) => {
    const selectedTrips = trips.filter((t) => selectedTripIds.includes(t.id));

    // const subTotal = selectedTrips.reduce(
    //   (sum, t) => sum + (Number(t.amount) || 0),
    //   0
    // );

    const tollCharges = selectedTrips.reduce(
      (sum, t) => sum + (Number(t.tollCharges) || 0),
      0
    );

    const handlingCharges = selectedTrips.reduce(
      (sum, t) => sum + (Number(t.handlingCharges) || 0),
      0
    );

    const otherCharges = selectedTrips.reduce(
      (sum, t) => sum + (Number(t.otherCharges) || 0),
      0
    );

    const grandTotal = tollCharges + handlingCharges + otherCharges;

    setTotals({
      tollCharges,
      handlingCharges,
      otherCharges,
      grandTotal,
    });
  };

  return (
    <div className="p-3">
      <Formik
        initialValues={{
          customerId: "",
          customerAddress: "",
          customerGSTNo: "",
          billDate: new Date(),
          tripIds: [],
        }}
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }) => {
          // Build final payload exactly as requested

          const selectedTripsData = trips.filter((t) =>
            values.tripIds.includes(t.id)
          );

          const totalKmTrip = selectedTripsData.reduce(
            (sum, t) => sum + (Number(t.kmOrTrip) || 0),
            0
          );

          // const totalKmTripRate = selectedTripsData.reduce(
          //   (sum, t) => sum + (Number(t.rate) || 0),
          //   0
          // );

          const totalKmTripRate = selectedTripsData[0]?.rate || 0;

          const d = values.billDate;
          const formattedBillDate =
            ("0" + d.getDate()).slice(-2) +
            "-" +
            ("0" + (d.getMonth() + 1)).slice(-2) +
            "-" +
            d.getFullYear();
          const payload = {
            sessionId: trips?.[0]?.sessionId || null,
            billDate: formattedBillDate,
            customerId: values.customerId,
            customerAddress: values.customerAddress || "",
            customerGSTNo: values.customerGSTNo || "",
            tollCharges: totals.tollCharges,
            otherCharges: totals.otherCharges,
            handlingCharges: totals.handlingCharges,
            totalAmount: totals.grandTotal,
            kmOrTrip: totalKmTrip || 0,
            rate: totalKmTripRate || 0,
            note: values.note || "",
            chargesInvoiceRequests: values.tripIds.map((id) => ({
              dailyTripId: id,
            })),
          };

          if (onSubmit) onSubmit(payload);
          resetForm();
          dispatch(resetTrips());
          onCloseForm();
        }}
      >
        {({ values, setFieldValue, setFieldError }) => {
          // Handler used for trip checkbox changes
          const handleTripToggle = (tripId, checked) => {
            // update tripIds
            const current = Array.isArray(values.tripIds)
              ? [...values.tripIds]
              : [];
            let updated;
            if (checked) {
              updated = current.includes(tripId)
                ? current
                : [...current, tripId];
            } else {
              updated = current.filter((id) => id !== tripId);
            }

            // set Formik tripIds
            setFieldValue("tripIds", updated);

            // Recalculate totals
            calculateTotals(updated);

            const selectedTrips = trips.filter((t) => updated.includes(t.id));
            const uniqueCustomerIds = [
              ...new Set(
                selectedTrips.map((t) => t.customerId).filter(Boolean)
              ),
            ];

            if (uniqueCustomerIds.length === 1) {
              const custId = uniqueCustomerIds[0];

              setFieldValue("customerId", custId);

              const customerObj = customersList.find((c) => c.id === custId);

              setFieldValue("customerAddress", customerObj?.address || "");
              setFieldValue("customerGSTNo", customerObj?.gstnNo || "");
              // clear any tripIds error about multiple customers
              setFieldError("tripIds", undefined);
            } else if (uniqueCustomerIds.length === 0) {
              // no trips selected
              setFieldValue("customerId", "");
              setFieldValue("customerAddress", "");
              setFieldValue("customerGSTNo", "");
              setFieldError("tripIds", undefined);
            } else {
              // multiple different customers selected -> invalid state
              setFieldValue("customerId", "");
              setFieldValue("customerAddress", "");
              setFieldValue("customerGSTNo", "");
              setFieldError(
                "tripIds",
                "Selected trips belong to multiple customers. Please pick trips for a single customer."
              );
            }
          };

          // SELECT ALL — adds/removes all trip IDs
          const handleSelectAll = (checked) => {
            // ALL trip IDs from the current list
            const allIds = checked ? trips.map((t) => t.id) : [];

            // Update Formik state
            setFieldValue("tripIds", allIds);

            // Recalculate totals
            calculateTotals(
              allIds,
              values.selectedTaxId,
              values.includeCharges
            );

            // Auto-select customer if needed
            const selectedTrips = trips.filter((t) => allIds.includes(t.id));
            const uniqueCustomerIds = [
              ...new Set(selectedTrips.map((t) => t.customerId)),
            ];

            if (uniqueCustomerIds.length === 1) {
              const custId = uniqueCustomerIds[0];
              const cust = customersList.find((c) => c.id === custId);

              setFieldValue("customerId", custId);
              setFieldValue("customerAddress", cust?.address || "");
              setFieldValue("customerGSTNo", cust?.gstnNo || "");
            } else {
              setFieldValue("customerId", "");
              setFieldValue("customerAddress", "");
              setFieldValue("customerGSTNo", "");
            }
          };

          return (
            <Form>
              <Row className="g-4">
                {/* Bill Date */}

                <Col md={3}>
                  {/* <Label>Session Year</Label>

                  <Select
                    options={session?.map((s) => ({
                      value: s.id,
                      label: s.name,
                    }))}
                    onChange={(opt) => {
                      const sessionId = opt ? opt.value : null;
                      dispatch(
                        fetchTripsList({
                          customerId: values.customerId,
                          sessionId: sessionId,
                          vehicleId: values.vehicleId,

                          isChargesBill: false,
                        })
                      );

                      setFieldValue("sessionIds", []);
                      calculateTotals([]);
                    }}
                    placeholder="Select Session Year"
                    isClearable
                  /> */}

                  <div>
                    <label className="form-label">
                      Session
                      <span className="text-danger">*</span>
                    </label>

                    {/* Auto-set Formik value (no useEffect) */}
                    {activeSessions &&
                      values.sessionId !== activeSessions?.id &&
                      setFieldValue("sessionId", activeSessions?.id)}

                    {/* Readonly input to show session name */}
                    <input
                      type="text"
                      className="form-control"
                      value={activeSessions?.name || ""}
                      disabled
                    />

                    {/* Hidden Formik input */}
                    <input
                      type="hidden"
                      name="sessionId"
                      value={values.sessionId}
                    />

                    {/* {touched.sessionId && errors.sessionId && (
                                        <div className="text-danger small">
                                          {errors.sessionId}
                                        </div>
                                      )} */}
                  </div>
                </Col>

                <Col md={3}>
                  <Label>
                    Customer <span className="text-danger">*</span>
                  </Label>

                  <Select
                    options={customersList.map((c) => ({
                      value: c.id,
                      label: c.name,
                    }))}
                    value={
                      values.customerId
                        ? {
                            value: values.customerId,
                            label: customersList.find(
                              (c) => c.id === values.customerId
                            )?.name,
                          }
                        : null
                    }
                    onChange={(opt) => {
                      setFieldValue("customerId", opt?.value);

                      dispatch(
                        fetchTripsList({
                          customerId: opt?.value,
                          sessionId: values?.sessionId,

                          isChargesBill: false,
                        })
                      );

                      setFieldValue("tripIds", []);
                      calculateTotals([]);
                    }}
                    placeholder="Select Customer"
                  />

                  <ErrorMessage
                    name="customerId"
                    component="div"
                    className="text-danger small"
                  />
                </Col>

                <Col md={3}>
                  <Label>Vehicle</Label>

                  <Select
                    options={vehicles?.map((v) => ({
                      value: v.id,
                      label: v.vehicleNo,
                    }))}
                    onChange={(opt) => {
                      const vehicleId = opt ? opt.value : null;
                      setFieldValue("vehicleId", vehicleId);

                      dispatch(
                        fetchTripsList({
                          customerId: values.customerId,
                          vehicleId: vehicleId,
                          sessionId: values?.sessionId,
                          isChargesBill: false,
                        })
                      );

                      setFieldValue("tripIds", []);
                      calculateTotals([]);
                    }}
                    placeholder="Select Vehicle"
                    isClearable
                  />
                </Col>
                <Col md={3}>
                  <Label>Supplier</Label>

                  <Select
                    options={suppliers?.map((s) => ({
                      value: s.id,
                      label: s.name,
                    }))}
                    onChange={(opt) => {
                      const supplierId = opt ? opt.value : null;
                      setFieldValue("supplierId", supplierId);

                      dispatch(
                        fetchTripsList({
                          customerId: values.customerId,
                          vehicleId: values.vehicleId,
                          supplierId: supplierId,
                          sessionId: values?.sessionId,

                          isChargesBill: false,
                        })
                      );

                      setFieldValue("tripIds", []);
                      calculateTotals([], values.selectedTaxId);
                    }}
                    placeholder="Select Vehicle"
                    isClearable
                  />
                </Col>

                <Col md={3}>
                  <Label>
                    Bill Date <span className="text-danger">*</span>
                  </Label>
                  <div className="input-group">
                    <Flatpickr
                      className="form-control"
                      placeholder="Select Bill Date"
                      value={values.billDate}
                      onChange={(dates) => {
                        setFieldValue("billDate", dates[0]);
                      }}
                      options={{
                        defaultDate: new Date(),
                        dateFormat: "d-m-Y",
                      }}
                    />

                    <ErrorMessage
                      name="billDate"
                      component="div"
                      className="text-danger small"
                    />
                    <div
                      className="input-group-text"
                      style={{ cursor: "pointer" }}
                    >
                      <i className="ri-calendar-2-line"></i>
                    </div>
                  </div>
                </Col>

                <Col md={3}>
                  <Label>From Date</Label>
                  <div className="input-group">
                    <Flatpickr
                      className="form-control"
                      placeholder="Select From Date"
                      options={{ dateFormat: "Y-m-d" }}
                      value={values.fromDate}
                      onChange={(date) => {
                        const fromDateValue = date?.[0]
                          ? (() => {
                              const d0 = date[0];
                              const y = d0.getFullYear();
                              const m = String(d0.getMonth() + 1).padStart(
                                2,
                                "0"
                              );
                              const dd = String(d0.getDate()).padStart(2, "0");
                              return `${y}-${m}-${dd}`;
                            })()
                          : null;
                        setFieldValue("fromDate", fromDateValue);

                        dispatch(
                          fetchTripsList({
                            customerId: values.customerId,
                            sessionId: values.sessionId,
                            fromDate: fromDateValue,
                            toDate: values.toDate,

                            isChargesBill: false,
                          })
                        );

                        calculateTotals(
                          values.sessionIds,
                          values.selectedTaxId
                        );
                      }}
                    />
                    <div
                      className="input-group-text"
                      style={{ cursor: "pointer" }}
                    >
                      <i className="ri-calendar-2-line"></i>
                    </div>
                  </div>
                </Col>

                <Col md={3}>
                  <Label>To Date</Label>
                  <div className="input-group">
                    <Flatpickr
                      className="form-control"
                      placeholder="Select To Date"
                      options={{ dateFormat: "Y-m-d" }}
                      value={values.toDate}
                      onChange={(date) => {
                        const toDateValue = date?.[0]
                          ? (() => {
                              const d0 = date[0];
                              const y = d0.getFullYear();
                              const m = String(d0.getMonth() + 1).padStart(
                                2,
                                "0"
                              );
                              const dd = String(d0.getDate()).padStart(2, "0");
                              return `${y}-${m}-${dd}`;
                            })()
                          : null;

                        setFieldValue("toDate", toDateValue);

                        dispatch(
                          fetchTripsList({
                            customerId: values.customerId,
                            sessionId: values.sessionId,
                            fromDate: values.fromDate,
                            toDate: toDateValue,
                            pageNo: 0,
                            isBill: false,
                          })
                        );

                        calculateTotals(
                          values.sessionIds,
                          values.selectedTaxId
                        );
                      }}
                    />

                    <div
                      className="input-group-text"
                      style={{ cursor: "pointer" }}
                    >
                      <i className="ri-calendar-2-line"></i>
                    </div>
                  </div>
                </Col>
                {/* TRIP LIST */}
                <Col md={12}>
                  <div className="d-flex justify-content-between align-items-center">
                    <Label>Select Trips *</Label>

                    <div style={{ width: "285px" }}>
                      <Label className="mb-0">Note:</Label>
                      <Input
                        type="text"
                        value={values.note}
                        onChange={(e) => setFieldValue("note", e.target.value)}
                        placeholder="Enter note"
                      />
                    </div>
                  </div>

                  <Table bordered responsive className="mt-2">
                    <thead>
                      <tr>
                        <th style={{ width: 60 }}>
                          <Input
                            type="checkbox"
                            checked={
                              values.tripIds.length > 0 &&
                              values.tripIds.length === trips.length
                            }
                            onChange={(e) => handleSelectAll(e.target.checked)}
                          />
                        </th>
                        <th>Customer</th>
                        <th>Supplier</th>
                        <th>Date</th>
                        <th>Vehicle</th>
                        <th>Rate</th>
                        <th>KM/Trip</th>
                        <th>Amount</th>
                        <th>Toll Charges</th>
                        <th>Handling Charges</th>
                        <th>Other Charges</th>
                      </tr>
                    </thead>

                    <tbody>
                      {loading && (
                        <tr>
                          <td colSpan="9" className="text-center">
                            Loading...
                          </td>
                        </tr>
                      )}

                      {!loading && trips.length === 0 && (
                        <tr>
                          <td
                            colSpan="9"
                            className="text-center text-muted py-4"
                          >
                            <i className="ri-emotion-unhappy-line fs-2 d-block mb-1"></i>
                            No Record found.
                          </td>
                        </tr>
                      )}

                      {!loading &&
                        trips.map((trip) => {
                          const checked = (values.tripIds || []).includes(
                            trip.id
                          );
                          return (
                            <tr key={trip.id}>
                              <td>
                                <Input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) =>
                                    handleTripToggle(trip.id, e.target.checked)
                                  }
                                />
                              </td>

                              <td>{trip.customerName}</td>
                              <td>{trip.suppliersName}</td>
                              <td>{trip.tripDate}</td>
                              <td>{trip.vehicleNo}</td>
                              <td>{trip.rate}</td>
                              <td>{trip.kmOrTrip}</td>
                              <td>{trip.amount}</td>
                              <td>{trip.tollCharges}</td>
                              <td>{trip.handlingCharges}</td>
                              <td>{trip.otherCharges}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </Table>

                  <ErrorMessage
                    name="tripIds"
                    component="div"
                    className="text-danger small"
                  />
                </Col>

                {/* BILL SUMMARY */}
                {/* BILL SUMMARY */}
                <Col md={12} className="mt-3">
                  <Table bordered>
                    <thead>
                      <tr>
                        <th>Toll Charges</th>
                        <th>Handling Charges</th>
                        <th>Other Charges</th>
                        <th>Grand Total</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr>
                        <td>₹ {Number(totals.tollCharges || 0).toFixed(2)}</td>
                        <td>
                          ₹ {Number(totals.handlingCharges || 0).toFixed(2)}
                        </td>
                        <td>₹ {Number(totals.otherCharges || 0).toFixed(2)}</td>
                        <td>
                          <b>₹ {Number(totals.grandTotal || 0).toFixed(2)}</b>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>

                {/* SUBMIT */}
                <Col md={12} className="text-end">
                  <Button color="success" className="btn-sm" type="submit">
                    Generate Bill
                  </Button>
                </Col>
              </Row>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default GenerateExtraChargesBillForm;
